import { type ChangeEvent, useState, type ReactNode  } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Divider, FormControlLabel, Stack, Switch } from '@mui/material';

import {
  partnerClientsExtendedQuery,
  partnerClientsStatsQuery,
} from '@/entities/partner';
import { useImpersonation } from '@/features/impersonation';
import { useAppQuery } from '@/shared/lib/useAppQuery';
import { Paper } from '@/shared/ui';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { Button } from '@/shared/ui/lib/Button';
import { TextField } from '@/shared/ui/lib/TextField';
import { CustomTypography } from '@/shared/ui/Typography';
import { dayjs } from '@/shared/utils/dayjs';

import { formatAffiliateAmount } from '../formatAmount';
import { AddClientDialog } from '../dialogs/AddClientDialog';
import { formatClientDisplayId } from '../clientDisplayId';

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY') : '—';
};

export function ClientsTab(): ReactNode {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [enteringId, setEnteringId] = useState<string | null>(null);
  const { enter: enterImpersonation } = useImpersonation();

  const { data: stats, isError: statsIsError } = useAppQuery({ ...partnerClientsStatsQuery() });
  const { data: clients, isPending, isError: clientsIsError } = useAppQuery({
    ...partnerClientsExtendedQuery({
      search: search.trim() || undefined,
      showInactive,
    }),
  });

  const handleEnter = async (clientAccountId: string, clientName: string) => {
    setEnteringId(clientAccountId);
    try {
      await enterImpersonation({ clientAccountId, clientName });
    } catch (e) {
      // The hook reloads on success; reaching this branch means the API
      // call failed (404 not_owned, 422 client_user_not_found, 503).
      setEnteringId(null);
    }
  };

  const items = clients?.items ?? [];
  const payingCount = statsIsError ? 0 : stats?.payingCount ?? 0;
  const everPaidCount = statsIsError ? 0 : stats?.anyPaymentCount ?? 0;

  if (clientsIsError) {
    return <Alert severity="error">Не удалось загрузить список клиентов</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: '20px' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
          <Stack direction="row" spacing={4}>
            <Stack spacing={0.5}>
              <CustomTypography variant="caption" color="text.secondary">
                {t('common:affiliatePage.clientsTab.payingCount')}
              </CustomTypography>
              <CustomTypography variant="h5" sx={{ fontWeight: 700 }}>
                {payingCount}
              </CustomTypography>
            </Stack>
            <Stack spacing={0.5}>
              <CustomTypography variant="caption" color="text.secondary">
                {t('common:affiliatePage.clientsTab.minOnePaymentCount')}
              </CustomTypography>
              <CustomTypography variant="h5" sx={{ fontWeight: 700 }}>
                {everPaidCount}
              </CustomTypography>
            </Stack>
          </Stack>
          <Button variant="contained" onClick={() => setAddOpen(true)}>
            + {t('common:affiliatePage.clientsTab.addClient')}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: '20px' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          mb={2}
        >
          <TextField
            size="small"
            placeholder={t('common:affiliatePage.clientsTab.searchPlaceholder') ?? ''}
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          <FormControlLabel
            control={
              <Switch checked={showInactive} onChange={(_, v) => setShowInactive(v)} />
            }
            label={t('common:affiliatePage.clientsTab.showInactive') ?? ''}
          />
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr 140px 140px 100px 130px 160px 80px',
              gap: 1,
              px: 2,
              py: 1,
              opacity: 0.6,
              minWidth: 1100,
            }}
          >
            <CustomTypography variant="caption">{t('common:affiliatePage.clientsTab.cols.id')}</CustomTypography>
            <CustomTypography variant="caption">{t('common:affiliatePage.clientsTab.cols.client')}</CustomTypography>
            <CustomTypography variant="caption">{t('common:affiliatePage.clientsTab.cols.registered')}</CustomTypography>
            <CustomTypography variant="caption">{t('common:affiliatePage.clientsTab.cols.subscriptionEnd')}</CustomTypography>
            <CustomTypography variant="caption">{t('common:affiliatePage.clientsTab.cols.autoPay')}</CustomTypography>
            <CustomTypography variant="caption">{t('common:affiliatePage.clientsTab.cols.paid')}</CustomTypography>
            <CustomTypography variant="caption">{t('common:affiliatePage.clientsTab.cols.status')}</CustomTypography>
            <CustomTypography variant="caption" />
          </Box>
          <Divider />

          {isPending && !clients ? (
            <Loader containerSize="medium" />
          ) : items.length === 0 ? (
            <NotFoundData
              title={t('common:affiliatePage.clientsNotFoundTitle')}
              caption={t('common:affiliatePage.clientsNotFoundCaption')}
            />
          ) : (
            items.map((c) => (
              <Box
                key={c.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr 140px 140px 100px 130px 160px 80px',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'custom.black.10',
                  alignItems: 'center',
                  minWidth: 1100,
                }}
              >
                <CustomTypography sx={{ fontFamily: 'monospace' }}>
                  {formatClientDisplayId(c.accountSupportId)}
                </CustomTypography>
                <Stack spacing={0}>
                  <CustomTypography>{c.name}</CustomTypography>
                  <CustomTypography variant="caption" color="text.secondary">
                    {c.email}
                  </CustomTypography>
                </Stack>
                <CustomTypography>{formatDate(c.createdAt)}</CustomTypography>
                <CustomTypography>{formatDate(c.subscriptionEndsAt)}</CustomTypography>
                <CustomTypography>
                  {c.hasAutoPay === null
                    ? '—'
                    : c.hasAutoPay
                    ? t('common:affiliatePage.clientsTab.autoPayYes') ?? 'Да'
                    : t('common:affiliatePage.clientsTab.autoPayNo') ?? 'Нет'}
                </CustomTypography>
                <CustomTypography>
                  {c.totalPaidMinor === null
                    ? '—'
                    : formatAffiliateAmount(c.totalPaidMinor / 100, c.currency)}
                </CustomTypography>
                <CustomTypography
                  sx={{
                    color: (theme) =>
                      c.status === 'active' ? theme.palette.actions.go : theme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  {c.status === 'active'
                    ? t('common:affiliatePage.status.active')
                    : t('common:affiliatePage.status.inactive')}
                </CustomTypography>
                {c.accountId ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleEnter(c.accountId!, c.name)}
                    disabled={enteringId === c.accountId}
                  >
                    {enteringId === c.accountId
                      ? t('common:affiliatePage.clientsTab.entering', { defaultValue: 'Входим…' })
                      : t('common:affiliatePage.clientsTab.enterCabinet', { defaultValue: 'Войти' })}
                  </Button>
                ) : (
                  <CustomTypography variant="caption" color="text.secondary">
                    —
                  </CustomTypography>
                )}
              </Box>
            ))
          )}
        </Box>
      </Paper>

      <AddClientDialog open={addOpen} onClose={() => setAddOpen(false)} />
    </Stack>
  );
}
